import React, { useRef, useCallback, useState, useEffect, useMemo } from "react";
import { ZoomKeyframe } from "../../../../types";
import { ZoomKeyframeBlock } from "./zoom-keyframe-block";
import {
  frameToItemPercent,
  pixelDeltaToFrameDelta,
  clampFrame,
  wouldOverlap,
} from "../../../../utils/zoom/zoom-timeline-utils";
import { validateKeyframes } from "../../../../utils/zoom/zoom-calculator";

interface TimelineZoomOverlayProps {
  keyframes: ZoomKeyframe[];
  itemDurationFrames: number;
  fps: number;
  selectedKeyframeId: string | null;
  onKeyframeSelect: (keyframeId: string | null) => void;
  onKeyframeUpdate: (keyframeId: string, updates: Partial<ZoomKeyframe>) => void;
  onKeyframeDelete: (keyframeId: string) => void;
  onKeyframeAdd: (startFrame: number, endFrame: number, scale: number) => void;
  disabled?: boolean;
  currentFrame?: number;
}

interface DraggingState {
  keyframeId: string;
  mode: "move" | "resize-left" | "resize-right";
  startX: number;
  startFrame: number;
  endFrame: number;
}

/**
 * Timeline zoom overlay component that renders zoom keyframe blocks on top of video timeline items.
 * Handles selection, dragging, and resizing of zoom keyframes.
 */
export const TimelineZoomOverlay: React.FC<TimelineZoomOverlayProps> = ({
  keyframes,
  itemDurationFrames,
  fps,
  selectedKeyframeId,
  onKeyframeSelect,
  onKeyframeUpdate,
  onKeyframeDelete,
  onKeyframeAdd,
  disabled = false,
  currentFrame,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingState, setDraggingState] = useState<DraggingState | null>(null);

  // Calculate keyframe positions
  const keyframePositions = useMemo(() => {
    return keyframes.map((kf) => ({
      ...kf,
      leftPercent: frameToItemPercent(kf.startFrame, itemDurationFrames),
      widthPercent: frameToItemPercent(kf.endFrame - kf.startFrame, itemDurationFrames),
    }));
  }, [keyframes, itemDurationFrames]);

  // Handle drag start
  const handleDragStart = useCallback(
    (keyframeId: string) =>
      (mode: "move" | "resize-left" | "resize-right", e: React.MouseEvent) => {
        if (disabled) return;

        const keyframe = keyframes.find((kf) => kf.id === keyframeId);
        if (!keyframe) return;

        setDraggingState({
          keyframeId,
          mode,
          startX: e.clientX,
          startFrame: keyframe.startFrame,
          endFrame: keyframe.endFrame,
        });
      },
    [keyframes, disabled]
  );

  // Handle mouse move during drag
  useEffect(() => {
    if (!draggingState || !containerRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 0;
      if (containerWidth === 0) return;

      const deltaX = e.clientX - draggingState.startX;
      const deltaFrames = pixelDeltaToFrameDelta(deltaX, containerWidth, itemDurationFrames);

      let newStartFrame = draggingState.startFrame;
      let newEndFrame = draggingState.endFrame;

      const minDuration = fps; // Minimum 1 second

      if (draggingState.mode === "move") {
        newStartFrame = clampFrame(draggingState.startFrame + deltaFrames, 0, itemDurationFrames);
        const duration = draggingState.endFrame - draggingState.startFrame;
        newEndFrame = newStartFrame + duration;

        // Clamp to container bounds
        if (newEndFrame > itemDurationFrames) {
          newEndFrame = itemDurationFrames;
          newStartFrame = newEndFrame - duration;
        }
      } else if (draggingState.mode === "resize-left") {
        newStartFrame = clampFrame(
          draggingState.startFrame + deltaFrames,
          0,
          draggingState.endFrame - minDuration
        );
      } else if (draggingState.mode === "resize-right") {
        newEndFrame = clampFrame(
          draggingState.endFrame + deltaFrames,
          draggingState.startFrame + minDuration,
          itemDurationFrames
        );
      }

      // Check for overlaps
      if (wouldOverlap(newStartFrame, newEndFrame, keyframes, draggingState.keyframeId)) {
        return; // Don't update if it would cause overlap
      }

      // Validate keyframes
      const newKeyframes = keyframes.map((kf) =>
        kf.id === draggingState.keyframeId
          ? { ...kf, startFrame: newStartFrame, endFrame: newEndFrame }
          : kf
      );

      const validation = validateKeyframes(newKeyframes);
      if (!validation.valid) {
        return;
      }

      onKeyframeUpdate(draggingState.keyframeId, {
        startFrame: newStartFrame,
        endFrame: newEndFrame,
      });
    };

    const handleMouseUp = () => {
      setDraggingState(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingState, keyframes, itemDurationFrames, fps, onKeyframeUpdate]);

  // Handle click on empty area (deselect)
  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      // Only deselect if clicking directly on the container, not on a keyframe
      if (e.target === containerRef.current) {
        onKeyframeSelect(null);
      }
    },
    [onKeyframeSelect]
  );

  // Handle context menu on container (for adding new keyframes)
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      // The context menu handling will be done by the parent component
      // This just prevents default behavior
      e.stopPropagation();
    },
    []
  );

  if (keyframes.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="timeline-zoom-overlay absolute inset-0 pointer-events-auto z-10"
      onClick={handleContainerClick}
      onContextMenu={handleContextMenu}
    >
      {keyframePositions.map((kf) => (
        <ZoomKeyframeBlock
          key={kf.id}
          keyframe={kf}
          leftPercent={kf.leftPercent}
          widthPercent={kf.widthPercent}
          isSelected={selectedKeyframeId === kf.id}
          isDragging={draggingState?.keyframeId === kf.id}
          onSelect={() => onKeyframeSelect(kf.id)}
          onDragStart={handleDragStart(kf.id)}
        />
      ))}
    </div>
  );
};
